import React, { useMemo, useState } from "react";
import { History as HistoryIcon, Search } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { MarketBadge, CompetitionLabel, ResultBadge } from "@/components/badges/Badges";
import { LoadingRows, ErrorState, EmptyState } from "@/components/common/States";
import { useLive } from "@/hooks/useAtlas";
import { useLaunch } from "@/context/LaunchContext";
import { visibleHistoryRows } from "@/presentation/entitlements";
import { normalizeCompetition } from "@/presentation/competitionNormalizer";
import { lifecycleOf, resultSurface } from "@/presentation/resultPresentation";
import { presentOdd } from "@/presentation/oddsPresentation";
import { scoreLine } from "@/lib/format";
import { SignalStatus } from "@/types/api";
import { useI18n } from "@/i18n";

type Filter = "ALL" | "PENDING" | "WIN" | "LOSE";

export default function History() {
  const { d, lang } = useI18n();
  const { data, isLoading, isError, error, refetch } = useLive(120);
  // FREE has no LIVE access: history shows RESOLVED rows only — no PENDING/current
  // LIVE signal preview (team/market/minute/score/event-id). Paid plans unchanged.
  const canLive = useLaunch().hasPlan("PRO");
  const [filter, setFilter] = useState<Filter>("ALL");
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    // Strip PENDING/current rows for FREE BEFORE any tab/search so hidden signals
    // can never be revealed via filters.
    let r = visibleHistoryRows(data?.rows || [], canLive);
    if (filter !== "ALL") r = r.filter((x) => lifecycleOf(x.status, x.won, { resolved_at: x.resolved_at, final_home_goals: x.final_home_goals, final_away_goals: x.final_away_goals }) === filter);
    if (q) r = r.filter((x) => `${x.home_team} ${x.away_team} ${normalizeCompetition(x.country, x.league).display} ${x.market_code}`.toLowerCase().includes(q.toLowerCase()));
    return r.slice(0, 120);
  }, [data, filter, q, canLive]);

  // FREE: hide the "Bekliyor" (pending) tab entirely.
  const tabs: Filter[] = canLive ? ["ALL", "PENDING", "WIN", "LOSE"] : ["ALL", "WIN", "LOSE"];
  const tabLabel = (t: Filter) => t === "ALL" ? d.common.all : t === "PENDING" ? d.result.pending : t === "WIN" ? d.result.win : d.result.lose;

  return (
    <div data-testid="history-page">
      <PageHeader title={d.nav.history}
        subtitle={lang === "tr" ? "Tüm sinyaller ve sonuçları." : "All signals and their results."} />

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex w-fit max-w-full gap-1 overflow-x-auto rounded-xl border border-white/10 bg-white/[0.03] p-1" data-testid="history-tabs">
          {tabs.map((t) => (
            <button key={t} onClick={() => setFilter(t)} data-testid={`history-tab-${t.toLowerCase()}`}
              className={`shrink-0 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition ${filter === t ? "bg-atlas/15 text-atlas-300" : "text-muted-foreground hover:text-foreground"}`}>
              {tabLabel(t)}
            </button>
          ))}
        </div>
        <div className="relative lg:max-w-xs lg:flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "tr" ? "Ara…" : "Search…"} data-testid="history-search"
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] py-2.5 pl-9 pr-3 text-sm text-foreground outline-none focus:border-atlas/40" />
        </div>
      </div>

      {isLoading ? <LoadingRows count={8} /> : isError ? <ErrorState error={error as any} onRetry={refetch} />
        : rows.length === 0 ? <EmptyState title={d.states.emptyHistory} icon={<HistoryIcon className="h-6 w-6" />} />
        : (
          <div className="space-y-2" data-testid="history-list">
            {rows.map((s) => {
              const key = lifecycleOf(s.status, s.won, { resolved_at: s.resolved_at, final_home_goals: s.final_home_goals, final_away_goals: s.final_away_goals });
              const resolved = key !== "PENDING";
              const surf = resultSurface(key);
              return (
                <div key={s.id} className={`panel panel-hover relative grid grid-cols-1 items-center gap-3 overflow-hidden p-4 pl-5 sm:grid-cols-[1.3fr_1fr_auto] ${surf.card}`} data-testid="history-row">
                  <span className={`absolute inset-y-0 left-0 w-1 ${surf.accent}`} aria-hidden />
                  <div className="flex items-center gap-3">
                    <CompetitionLabel country={s.country} league={s.league} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">{s.home_team} <span className="text-muted-foreground">vs</span> {s.away_team}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <MarketBadge code={s.market_code} backendLabel={s.market_name} size="sm" />
                      {(() => { const o = presentOdd({ value: s.odd, source: null }); return o.show ? <span className="num text-xs text-atlas-300">{d.common.refOdds} {o.value}</span> : null; })()}
                      {s.minute != null && <span className="num text-[11px] text-muted-foreground">Sinyal {s.minute}'</span>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <div className="text-right">
                      <div className="num text-base font-bold text-foreground">{resolved ? scoreLine(s.final_home_goals, s.final_away_goals) : scoreLine(s.initial_home_goals, s.initial_away_goals)}</div>
                      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{resolved ? d.common.finalScore : d.common.score}</div>
                    </div>
                    <ResultBadge status={s.status} won={s.won} isLive={key === "PENDING"} size="sm" />
                    <span className="num hidden text-[10px] text-muted-foreground md:inline">#{s.id}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
